import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { searchContext } from '../../../../context/Context';
import { DATASETS } from '../../../Constants';
import updateParams from '../../../../utils/updateParams';
import dropdownLists from '../../DropdownLists';
import { filterTypes } from '../../SetupTablesResources';
import { getGeneNCBIid } from '../renders/MappingFunctions';
import { renderRoundedValues, renderListsFromObjects } from '../renders/CommonAtributes';
import { ButtonInfoGeneContent, OpenPopup } from '../components/index';
import { EXT_NCBI_GENE_LINK, EXT_UNIPROT_LINK, EXT_ENSEMBL_LINK } from '../config/constants';
import { getHeaderProps } from '../config/helpers';
import { createSelectFilter, createCapitalizeFilter } from '../filters/filterConfigs';
import { renderGeneNCBILink, renderEnsemblLinks, renderUniProtLinks } from '../renders/linkRenderers';

export const getGeneColumns = (context) => {
  const { tab } = context;
  const headerProps = getHeaderProps();

  return [
    {
      name: 'symbol',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Symbol">Gene</span>,
      uniqueID: 'Gene',
      headerProps,
      columnSize: null,
      minWidth: 200,
      render: ({ data }) => {
        const { tab } = searchContext();
        let ids = [];
        let values = [];
        let geneInfo = undefined;
        let isGene = false;
        const delimiter = '|';

        if (tab === 'GENES') {
          isGene = true;
          ids = [data.ncbiID];
          values = [data.symbol];
          geneInfo = { symbol: data.symbol, id: data.ncbiID };
        }

        const formattedLinks = ids?.map((id, index) => (
          <span key={id}>
            <a href={`${EXT_NCBI_GENE_LINK}${id}`} target="_blank" rel="noopener noreferrer">
              {values[index]}
              {id && <i className="fa fa-external-link px-1" aria-hidden="true"></i>}
            </a>
            <span className="px-1">{index !== ids?.length - 1 && delimiter}</span>
          </span>
        ));

        return (
          <span className="geneModalLink">
            {formattedLinks}
            {isGene && <ButtonInfoGeneContent value={geneInfo} />}
          </span>
        );
      },
    },

    {
      name: 'geneSymbol',
      category: 'Gene',
      sortable: true,
      filterType: filterTypes.stringWithContains,
      header: <span title="Gene Symbol">Gene</span>,
      headerProps,
      minWidth: 200,
      render: ({ data }) => {
        const { tab } = searchContext();
        let ids = [];
        let values = [];
        let geneInfo = undefined;
        const delimiter = '|';

        if (tab === 'GDA' || tab === 'DISEASES' || tab === 'GENES') {
          ids = [data.geneNcbiID];
          values = [data.geneSymbol];
          geneInfo = { symbol: data.geneSymbol, id: data.geneNcbiID };
        } else if (tab === 'DDA') {
          if (data.geneNcbiID) {
            ids = [data.geneNcbiID];
          } else if (data.geneNcbiIDs) {
            ids = data.geneNcbiIDs;
          }
          values = [data.geneSymbol];
          geneInfo = { symbol: data.geneSymbol, id: data.geneNcbiID || data.geneNcbiIDs };
        } else if (tab === 'VDA' && !data.geneNcbiIDs?.length) {
          return '';
        } else if (tab === 'VDA' && data.geneNcbiIDs?.length === data.geneSymbol?.length) {
          ids = data.geneNcbiIDs;
          values = data.geneSymbol;
        } else if (tab === 'VARIANTS') {
          ids = data.variantToGenes?.map(element => element.geneNcbiID);
          values = data.variantToGenes?.map(element => element.geneSymbol);
        }

        const formattedLinks = ids?.map((id, index) => (
          <span key={id}>
            <a href={`${EXT_NCBI_GENE_LINK}${id}`} target="_blank" rel="noopener noreferrer">
              {values[index]}
              {id && <i className="fa fa-external-link px-1" aria-hidden="true"></i>}
            </a>
            <span className="px-1">{index !== ids?.length - 1 && delimiter}</span>
          </span>
        ));

        return (
          <span className="geneModalLink">
            {formattedLinks}
            {tab === 'GDA' && <ButtonInfoGeneContent value={geneInfo} />}
          </span>
        );
      },
    },

    {
      name: 'description',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Full Name">Gene Full Name</span>,
      headerProps,
      minWidth: 295,
      show: ({ tab }) => tab === 'GENES', 
    },

    {
      name: 'geneDescription',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Full Name">Gene Full Name</span>,
      headerProps,
      minWidth: 295,
      show: ({ tab }) => tab !== 'GENES', 
    },

    {
      name: 'gene_ncbi_id',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: row => {
        const geneNcbis = row.data.gene_ncbi_id;
        const linkout = geneNcbis ? getGeneNCBIid(geneNcbis) : '';
        return <React.Fragment>{linkout}</React.Fragment>;
      },
    },

    {
      name: 'geneNcbiID',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: row => {
        const geneNcbis = row.data.geneNcbiID ? row.data.geneNcbiID : row.data.gene_ncbi_id;
        return renderGeneNCBILink(geneNcbis);
      },
    },

    {
      name: 'geneNcbiIDs',
      category: 'Gene',
      sortable: true,
      header: <span title="Genes NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: row => {
        const geneNcbis = row.data.geneNcbiIDs;
        const linkout = geneNcbis ? getGeneNCBIid(geneNcbis) : '';
        return <React.Fragment>{linkout}</React.Fragment>;
      },
    },

    {
      name: 'ncbiID',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI identifier">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: row => {
        const geneNcbis = row.data.ncbiID ? row.data.ncbiID : row.data.gene_ncbi_id;
        return renderGeneNCBILink(geneNcbis);
      },
    },

    {
      name: 'gene_ensembl_id',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => <span>{renderEnsemblLinks(value)}</span>,
    },

    {
      name: 'geneEnsemblIDs',
      category: 'Gene',
      sortable: tab === 'VARIANTS' ? false : true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => <span>{renderEnsemblLinks(value)}</span>,
    },

    {
      name: 'ensembl_ids',
      category: 'Gene',
      sortable: tab === 'VARIANTS' ? false : true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => <span>{renderEnsemblLinks(value)}</span>,
    },

    {
      name: 'geneNcbiType',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Type">Gene Type</span>,
      headerProps,
      minWidth: 125,
      ...createSelectFilter(dropdownLists.geneNcbiType),
    },

    {
      name: 'ncbi_type',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Type">Gene Type</span>,
      headerProps,
      ...createSelectFilter(dropdownLists.ncbi_type),
    },

    {
      name: 'geneDPI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Disease Pleiotropy Index for the gene">
            DPI<sub>g</sub>
          </span>
          <OpenPopup popupType="showDPI" />
        </>
      ),
      headerProps,
      minWidth: 130,
    },

    {
      name: 'geneDSI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Disease Specificity Index for the gene">
            DSI<sub>g</sub>
          </span>
          <OpenPopup popupType="showDSI" />
        </>
      ),
      headerProps,
      minWidth: 180,
    },

    {
      name: 'genepLI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Probability of being loss of function intolerant">pLI</span>{' '}
          <OpenPopup popupType="showpLI" />
        </>
      ),
      headerProps,
    },

    {
      name: 'PLI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Probability of being loss of function intolerant">pLI</span>
          <OpenPopup popupType="showpLI" />
        </>
      ),
      headerProps,
      minWidth: 85,
      render: instance => renderRoundedValues(instance, 'PLI'),
    },

    {
      name: 'geneProteinClassNames',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Protein classification according to the DrugTarget Ontology">Protein Class</span>{' '}
          <OpenPopup popupType="showProteinClass" />
        </>
      ),
      minWidth: 150,
      headerProps,
      ...createSelectFilter(dropdownLists.geneProteinClassNames),
    },

    {
      name: 'geneProteinStrIDs',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="UniProt Accession">UniProt</span> <OpenPopup popupType="showUniProt" />
        </>
      ),
      headerProps,
      minWidth: 205,
      render: ({ data }) => {
        const { tab } = searchContext();
        let ids = [];
        let values = [];

        if (tab === 'GDA' || tab === 'DDA') {
          ids = data.geneProteinStrIDs;
          values = data.geneProteinStrIDs;
        }

        return <span className="geneModalLink">{renderUniProtLinks(ids, values)}</span>;
      },
    },

    {
      name: 'proteinClassIDs',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Protein classification according to the DrugTarget Ontology">Protein Class</span>
          <OpenPopup popupType="showProteinClass" />
        </>
      ),
      headerProps,
      minWidth: 200,
      ...createCapitalizeFilter(dropdownLists.proteinClassIDs),
      render: instance => {
        let proteinClassIDs = instance.data.proteins;
        let storedClasses = [];
        let listProteins = proteinClassIDs?.map(item => {
          let elList = [];
          item.proteinClasses.forEach((protClass, ix, list2) => {
            if (!storedClasses.includes(protClass.name)) {
              storedClasses.push(protClass.name);
              let el;
              if (list2?.length - 1 !== ix && storedClasses?.length > 1) {
                el = <span>{protClass.name} | </span>;
              } else {
                el = <span>{protClass.name}</span>;
              }
              elList.push(el);
            }
          });
          return elList;
        });
        return listProteins;
      },
    },

    {
      name: 'proteinIDs',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="UniProt Accession">UniProt</span> <OpenPopup popupType="showUniProt" />
        </>
      ),
      headerProps,
      minWidth: 150,
      render: instance => {
        let arr_uniprot = [];
        let instance_length = instance.data.proteinIDs?.length;

        for (let i = 0; i < instance_length; i++) {
          let uniprot = instance.data.proteinIDs[i];
          let link = `https://www.uniprot.org/uniprot/${uniprot}`;
          if (i !== instance_length - 1) {
            arr_uniprot.push(
              <span key={i}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {uniprot} <i className="fa fa-external-link" aria-hidden="true"></i>
                </a>{' '}
                |{' '}
              </span>,
            );
          } else {
            arr_uniprot.push(
              <span key={i}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {uniprot} <i className="fa fa-external-link" aria-hidden="true"></i>
                </a>
              </span>,
            );
          }
        }
        return arr_uniprot;
      },
    },

    {
      name: 'pathwayID',
      category: 'Gene',
      sortable: true,
      header: <span title="Pathways IDs">Pathways IDs</span>,
      render: instance => renderListsFromObjects(instance, 'pathways', 'pathwayID'),
      headerProps,
      minWidth: 295,
    },

    {
      name: 'pathwayName',
      category: 'Gene',
      sortable: true,
      header: <span title="Pathways Names">Pathways</span>,
      render: instance => renderListsFromObjects(instance, 'pathways', 'pathwayName'),
      headerProps,
      minWidth: 295,
    },

    {
      name: 'numDiseasesAssociatedToGene',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Number of diseases associated to the gene">
          N diseases<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const { tab } = searchContext();
        let newUrl = '';
        let newParams = '';
        let location = useLocation();
        
        if (tab === 'GENES') {
          newParams = updateParams({ view: DATASETS.GENES, tab: 'GDA', searchItems: [{ id: data.ncbiID }] });
        } else if (tab === 'GDA') {
          newParams = updateParams({
            view: DATASETS.GENES,
            tab: 'GDA',
            searchItems: [{ id: data.geneNcbiID }],
          });
        }
        
        newUrl = `${location.pathname}?${newParams}`;
        return (
          <Link className="tab-links" to={newUrl}>
            {data.numDiseasesAssociatedToGene}{' '}
          </Link>
        );
      },
    },

    {
      name: 'numVariantsAssociatedToGene',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Number of variants associated to the gene">
          N variants<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const { tab } = searchContext();
        let newUrl = '';
        let newParams = '';
        let location = useLocation();
        
        if (tab === 'GENES') {
          newParams = updateParams({ view: DATASETS.GENES, tab: 'VDA', searchItems: [{ id: data.ncbiID }] });
        } else if (tab === 'GDA') {
          newParams = updateParams({
            view: DATASETS.GENES,
            tab: 'VDA',
            searchItems: [{ id: data.geneNcbiID }],
          });
        }
        
        newUrl = `${location.pathname}?${newParams}`;
        return (
          <Link className="tab-links" to={newUrl}>
            {data.numVariantsAssociatedToGene}{' '}
          </Link>
        );
      },
    },
  ];
};